/**
 * Checks if HTML content contains a meta generator tag with the specified generator name.
 *
 * Uses a hybrid approach for performance:
 * - For content < 100KB: Uses DOM parser for accurate detection
 * - For content >= 100KB: Uses regex on first 5KB (meta tags are typically in <head>)
 *
 * @param html - The HTML content to check
 * @param generatorName - The generator name to look for (e.g., "Mem")
 * @returns true if a matching meta generator tag is found, false otherwise
 *
 * @example
 * ```typescript
 * // Returns true for:
 * // <meta name="generator" content="Mem">
 * // <meta name="Generator" content="Mem 1.0">
 * // <meta content="Mem" name="generator">
 * hasMetaGeneratorTag(html, 'Mem');
 * ```
 */
export function hasMetaGeneratorTag(html: string, generatorName: string): boolean {
  // Quick early exit if no meta tags at all
  if (!html || !html.includes("<meta")) {
    return false;
  }

  const SIZE_THRESHOLD = 100_000; // 100KB

  // For small/medium content, use DOM parser (most accurate)
  if (html.length < SIZE_THRESHOLD) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Get all meta tags and check each one
      const metaTags = doc.querySelectorAll("meta");

      return Array.from(metaTags).some(meta => {
        const name = meta.getAttribute("name");
        const content = meta.getAttribute("content");

        // Check if this is a generator meta tag with matching content
        return (
          name?.toLowerCase() === "generator" &&
          content?.toLowerCase().startsWith(generatorName.toLowerCase())
        );
      });
    } catch {
      return false;
    }
  }

  // For large content, only search the beginning (meta tags are typically in <head>)
  const headContent = html.slice(0, 5000);

  // Escape special regex characters in generator name
  const escapedName = generatorName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Check both attribute orders with flexible patterns
  const pattern1 = new RegExp(
    `<meta[^>]*\\bname\\s*=\\s*["']?generator["']?[^>]*\\bcontent\\s*=\\s*["']?${escapedName}`,
    "i"
  );
  const pattern2 = new RegExp(
    `<meta[^>]*\\bcontent\\s*=\\s*["']?${escapedName}[^>]*\\bname\\s*=\\s*["']?generator["']?`,
    "i"
  );

  return pattern1.test(headContent) || pattern2.test(headContent);
}
